import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import maintenanceArchiveService, {
  CreateArchiveRequest,
  ArchiveDocument,
  ArchiveFilters,
  ClientArchiveSummary
} from '../../services/maintenanceArchiveService';

interface MaintenanceArchiveState {
  archives: ArchiveDocument[];
  clientSummaries: ClientArchiveSummary[];
  currentArchive: ArchiveDocument | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  limit: number;
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isSummaryLoading: boolean;
  error: string | null;
  filters: ArchiveFilters;
}

const initialState: MaintenanceArchiveState = {
  archives: [],
  clientSummaries: [],
  currentArchive: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  limit: 10,
  isLoading: false,
  isCreating: false,
  isDeleting: false,
  isSummaryLoading: false,
  error: null,
  filters: {}
};

// Async thunks
export const createArchive = createAsyncThunk(
  'maintenanceArchive/create',
  async (data: CreateArchiveRequest, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.createArchive(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create archive');
    }
  }
);

export const fetchArchives = createAsyncThunk(
  'maintenanceArchive/fetchAll',
  async (filters: ArchiveFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.getArchives(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch archives');
    }
  }
);

export const fetchClientArchiveSummaries = createAsyncThunk(
  'maintenanceArchive/fetchClientSummaries',
  async (_, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.getClientArchiveSummary();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch client summaries');
    }
  }
);

export const fetchArchiveById = createAsyncThunk(
  'maintenanceArchive/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.getArchiveById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch archive');
    }
  }
);

export const updateArchive = createAsyncThunk(
  'maintenanceArchive/update',
  async ({ id, data }: { id: string; data: Partial<CreateArchiveRequest> }, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.updateArchive(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update archive');
    }
  }
);

export const deleteArchive = createAsyncThunk(
  'maintenanceArchive/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await maintenanceArchiveService.deleteArchive(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete archive');
    }
  }
);

export const restoreArchive = createAsyncThunk(
  'maintenanceArchive/restore',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceArchiveService.restoreArchive(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to restore archive');
    }
  }
);

export const downloadArchiveDocument = createAsyncThunk(
  'maintenanceArchive/download',
  async (id: string, { rejectWithValue }) => {
    try {
      const blob = await maintenanceArchiveService.downloadDocument(id);
      // Trigger download directly — Blob must not be stored in Redux state
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `archive-${id}`;
      link.click();
      URL.revokeObjectURL(url);
      return { id };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to download document');
    }
  }
);

const maintenanceArchiveSlice = createSlice({
  name: 'maintenanceArchive',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ArchiveFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentArchive: (state) => {
      state.currentArchive = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Create archive
    builder
      .addCase(createArchive.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createArchive.fulfilled, (state, action) => {
        state.isCreating = false;
        state.archives.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createArchive.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      });

    // Fetch archives
    builder
      .addCase(fetchArchives.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArchives.fulfilled, (state, action) => {
        state.isLoading = false;
        state.archives = action.payload.result || [];
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchArchives.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.archives = [];
      });

    // Fetch client summaries
    builder
      .addCase(fetchClientArchiveSummaries.pending, (state) => {
        state.isSummaryLoading = true;
        state.error = null;
      })
      .addCase(fetchClientArchiveSummaries.fulfilled, (state, action) => {
        state.isSummaryLoading = false;
        state.clientSummaries = action.payload;
      })
      .addCase(fetchClientArchiveSummaries.rejected, (state, action) => {
        state.isSummaryLoading = false;
        state.error = action.payload as string;
        state.clientSummaries = [];
      });

    // Fetch archive by ID
    builder
      .addCase(fetchArchiveById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArchiveById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentArchive = action.payload;
      })
      .addCase(fetchArchiveById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update archive
    builder
      .addCase(updateArchive.fulfilled, (state, action) => {
        const index = state.archives.findIndex(archive => archive._id === action.payload._id);
        if (index !== -1) {
          state.archives[index] = action.payload;
        }
        if (state.currentArchive?._id === action.payload._id) {
          state.currentArchive = action.payload;
        }
      })
      .addCase(updateArchive.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete archive
    builder
      .addCase(deleteArchive.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteArchive.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.archives = state.archives.filter(archive => archive._id !== action.payload);
        state.totalCount -= 1;
        if (state.currentArchive?._id === action.payload) {
          state.currentArchive = null;
        }
      })
      .addCase(deleteArchive.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });

    // Restore archive
    builder
      .addCase(restoreArchive.fulfilled, (state, action) => {
        const index = state.archives.findIndex(a => a._id === action.payload._id);
        if (index !== -1) {
          state.archives[index] = action.payload;
        }
        if (state.currentArchive?._id === action.payload._id) {
          state.currentArchive = action.payload;
        }
      })
      .addCase(restoreArchive.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Download document
    builder
      .addCase(downloadArchiveDocument.fulfilled, (state, action) => {
        // Handle download completion if needed
        // The actual download will be handled in the component
      })
      .addCase(downloadArchiveDocument.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { 
  setFilters, 
  clearFilters, 
  clearError, 
  clearCurrentArchive, 
  setPage 
} = maintenanceArchiveSlice.actions;

export default maintenanceArchiveSlice.reducer;